import { FC, useEffect, useRef, useState } from 'react';
import { FormGroup, Label, TextInput, Button } from '../../../components/ui';
import { useGoogleMaps } from '../../../hooks';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import {
    Autocomplete,
    Map,
    LatLng,
    LatLngBounds,
    Marker,
    Geocoder,
    GeocoderRequest,
    GeocoderResult
} from '../../../types/google';
import { Styles } from '../../../types/styles';
import useFormWizardContext from '../hooks/useFormWizardContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faMapMarkedAlt, faMap } from '@fortawesome/free-solid-svg-icons';
import useJurisdiction from '../../../hooks/useJurisdiction';
import useMounted from '../../../hooks/useMounted';
import applyTheme from '../../../utils/applyTheme';
import Wkt from 'wicket/wicket-gmap3';
import { getAddressFromComponents } from '../../../utils/google';

const styles: Styles = {
    map: {
        width: '100%',
        height: '300px',
    },
};

const Location: FC = () => {
    const addressRef = useRef<HTMLInputElement | null>(null);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const googleMapRef = useRef<Map | null>(null);
    const geocoderRef = useRef<Geocoder | null>(null);
    const autoCompleteRef = useRef<Autocomplete | null>(null);
    const markerClustererRef = useRef<MarkerClusterer | null>(null);
    const currentBoundsRef = useRef<LatLngBounds | null>(null);

    const [markers, setMarkers] = useState<Marker[]>([]);
    const [isDroppedPin, setIsDroppedPin] = useState<boolean>(false);

    const {
        requestMessageHTML,
        boundaryPoints,
        noLocationAddress,
        noLocationAddressLat,
        noLocationAddressLng,
        googleMapKey,
        requestTheme,
        presetLocation,
        polygonWkt,
        polygonsWkt
    } = useJurisdiction();
    
    const {
        data,
        setValue,
        validateStep
    } = useFormWizardContext();
    
    const { address, lat, lng } = data; 

    const {
        isLoading, google
    } = useGoogleMaps({
        key: googleMapKey || '',
        libraries: ['geometry', 'places'],
    });
    
    const handlePlaceSelect = () => {
        const addressObject = autoCompleteRef.current!.getPlace();
        
        if (!addressObject) {
            return null;
        }

        let addressString = addressObject.formatted_address ?? '';
        
        if (addressObject.address_components) {
            addressString = getAddressFromComponents(addressObject.address_components);
        }
        
        // setAddress(addressObject.formatted_address || '');
        setValue('address', addressString);
        
        return addressObject;
    };
    
    const handleDropPin = () => {
        const mapCenter = googleMapRef.current?.getCenter();
        
        if (mapCenter) {

            getReverseGeocode(mapCenter, (addressObject) => {
                setValue('address', addressObject.formatted_address);

                setNewRequestLocation(mapCenter);
            });

            googleMapRef.current?.setZoom(17);

            setIsDroppedPin(true);
        }
    };
    
    const handleRemovePin = () => {
        markerClustererRef.current!.clearMarkers();
        
        setValue('lat', lat ?? null);
        setValue('lng', lng ?? null);
        
        setIsDroppedPin(false);
    };
    
    const handleEnterAddress = () => {
        handleRemovePin();
    };
    
    const handleNoLocation = () => {
        handleRemovePin();
        
        setValue('isNoLocation', true);
        setValue('address', noLocationAddress);
        setValue('lat', noLocationAddressLat);
        setValue('lng', noLocationAddressLng);
    };
    
    const setNewRequestLocation: (location: LatLng) => void = (location) => {
        addLocationMarker(location);

        setValue('lat', location.lat());
        setValue('lng', location.lng());
    };
    
    const addLocationMarker: (location: LatLng) => void = (location) => {
        const newRequestMarker = new google!.maps.Marker({
            'position': location,
            'draggable': true
        });
        
        markerClustererRef.current!.clearMarkers();
        markerClustererRef.current!.addMarker(newRequestMarker);
        
        google!.maps.event.addListener(newRequestMarker, 'dragend', () => {
            const position = newRequestMarker.getPosition() as LatLng;
            
            setValue('lat', position.lat());
            setValue('lng', position.lng());

            getReverseGeocode(position, (addressObject) => {
                setValue('address', addressObject.formatted_address);
            });
        });
    };

    const getReverseGeocode: (location: LatLng, callback: (result: GeocoderResult) => void) => void = 
        (location, callback) => {
            geocoderRef.current!.geocode(
                {'latLng': location} as GeocoderRequest,
                (results, status) => {
                    if (status !== google!.maps.GeocoderStatus.OK) {
                        return;
                    }
                    
                    if (callback && results) {
                        callback(results[0]);
                    }
                }
            );
        };

    const getAddressGeocode: (address: string, callback: (result: GeocoderResult) => void) => void =
        (address, callback) => {
            geocoderRef.current!.geocode(
                {'address': address} as GeocoderRequest,
                (results, status) => {
                    if (status === google!.maps.GeocoderStatus.OK) {
                        if (callback && results) {
                            callback(results[0]);
                        }
                    }
                }
            );
        };
    
    const initializeGoogleMaps = () => {
        if (!google || !addressRef.current || !mapRef.current) {
            return;
        }
        
        autoCompleteRef.current = new google.maps.places.Autocomplete(
            addressRef.current as HTMLInputElement,
            {
                types: ['geocode'],
                componentRestrictions: {
                    country: 'us',
                },
            }
        );
        
        autoCompleteRef.current.addListener('place_changed', () => {
            const place = handlePlaceSelect();

            if (!googleMapRef.current) {
                return;
            }

            if (!place || !place.geometry) {
                return;
            }

            google.maps.event.addListenerOnce(googleMapRef.current, 'bounds_changed', () => {
                googleMapRef.current!.setZoom(17);
                googleMapRef.current!.panTo(place.geometry!.location as LatLng);
            });

            googleMapRef.current.fitBounds(
                new google.maps.LatLngBounds(
                    place.geometry.viewport?.getSouthWest(),
                    place.geometry.viewport?.getNorthEast()
                )
            );

            setNewRequestLocation(place.geometry.location as LatLng);
            // setServers(place);
        });

        googleMapRef.current = new google.maps.Map(mapRef.current as HTMLDivElement, {
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            zoom: 17,
        });

        autoCompleteRef.current.bindTo('bounds', googleMapRef.current);

        markerClustererRef.current = new MarkerClusterer(googleMapRef.current, markers, {
            maxZoom: 17,
            imagePath: '//raw.githubusercontent.com/googlemaps/js-marker-clusterer/gh-pages/images/m'
        });

        geocoderRef.current = new google.maps.Geocoder();

        google.maps.event.addDomListener(window, 'resize', () => {
            if (!currentBoundsRef.current) {
                return;
            }

            googleMapRef.current!.fitBounds(currentBoundsRef.current);
        });

        google.maps.event.addListener(googleMapRef.current, 'idle', () => {
            currentBoundsRef.current = googleMapRef.current!.getBounds() as LatLngBounds;
        });

        google.maps.event.addListenerOnce(googleMapRef.current, 'idle', () => {
            googleMapRef.current!.setZoom(17);

            try {
                if (boundaryPoints.length !== 0) {
                    const bounds = new google.maps.LatLngBounds();

                    boundaryPoints.forEach(boundaryPoint => {
                        bounds.extend(new google.maps.LatLng(boundaryPoint[1], boundaryPoint[0]));
                    });

                    googleMapRef.current?.fitBounds(bounds);
                }
            }
            catch (e) {}

            if (lat && lng) {
                const location = new google.maps.LatLng(lat, lng);

                googleMapRef.current!.panTo(location);

                addLocationMarker(location);
            }
        });

        void loadBoundary();
    };

    const loadBoundary = async () => {
        const map = googleMapRef.current;
        const wkt = new Wkt.Wkt();
        
        let polygons: string[] = [];

        const isMultipolygon = !!polygonsWkt;
        
        if (isMultipolygon) {
            polygons = polygonsWkt;
        }
        else {
            polygons = [polygonWkt];
        }

        const bounds = new google!.maps.LatLngBounds();
        let flag = true;
        for (const pWkt of polygons) {
            wkt.read(pWkt);

            let p = wkt.toObject();
            
            if (!Array.isArray(p)) {
                p = [p];
            }

            for (const polygon of (p as any[])) {
                polygon.setMap(map);

                // map.fitBounds(polygon.getBounds());
                polygon.setOptions({ fillOpacity: 0.05 });
            }

            // if ((p as any[])[0].getBounds) {
            //     map!.fitBounds((p as any[])[0].getBounds());
            // }
            
            (p as any[]).forEach((polygon) => {
                const paths = polygon.getPaths();

                paths.forEach((path: any) => {
                    for (let i = 0; i < path.getLength(); i++) {
                        bounds.extend(path.getAt(i));
                    }
                });
                
                if (flag) {
                    ((bounds) => {
                        setTimeout(() => {
                            map!.fitBounds(bounds);
                            map!.setCenter(bounds.getCenter());
                            map!.setZoom(17);
                        }, 500);
                    })(bounds);
                    
                    flag = false;
                }
            });
        }
    };

    useMounted(() => {
        applyTheme(requestTheme);
    });
    
    useEffect(initializeGoogleMaps, []);
    
    useEffect(initializeGoogleMaps, [google, addressRef.current, mapRef.current]);
    
    useEffect(() => {
        if (google && presetLocation) {
            const location = new google.maps.LatLng(presetLocation.latitude, presetLocation.longitude);
            
            setValue('location_name', presetLocation.name ?? '');
            setValue('email', presetLocation.email ?? '');
            setValue('address', presetLocation.address);
            setNewRequestLocation(location);
            
            google.maps.event.addListenerOnce(googleMapRef.current!, 'idle', () => {
                googleMapRef.current!.setZoom(17);

                googleMapRef.current!.panTo(location);
            });
        }
    }, [google, presetLocation]);
    
    useEffect(() => {
        validateStep(() => address.length > 0 || (!!lat && !!lng));
    }, [address, lat, lng]);
    
    return (
        <>
            {requestMessageHTML && (
                <div className="mb-4" dangerouslySetInnerHTML={{ __html: requestMessageHTML }} />
            )}
            
            <div className="flex mb-4 gap-4">
                {!isDroppedPin && (
                    <Button
                        color="secondary"
                        variant="outline"
                        onClick={handleDropPin}
                    >
                        <FontAwesomeIcon icon={faMapMarkerAlt} />&nbsp;
                        Drop Location Pin
                    </Button>
                )}
                
                {isDroppedPin && (
                    <Button
                        color="secondary"
                        variant="outline"
                        onClick={handleRemovePin}
                    >
                        <FontAwesomeIcon icon={faMapMarkerAlt} />&nbsp;
                        Remove Location Pin
                    </Button>
                )}
                
                <Button
                    color="secondary"
                    variant="outline"
                    onClick={handleEnterAddress}
                >
                    <FontAwesomeIcon icon={faMapMarkedAlt} />&nbsp;
                    Enter Address
                </Button>

                {noLocationAddress && noLocationAddressLat && noLocationAddressLng && (
                    <Button
                        color="danger"
                        variant="outline"
                        onClick={handleNoLocation}
                    >
                        <FontAwesomeIcon icon={faMap} />&nbsp;
                        No Location
                    </Button>
                )}
            </div>
            
            <FormGroup>
                <Label htmlFor="address">
                    Enter Address, City, State before clicking on the Google recommended location.
                </Label>
                <TextInput
                    ref={addressRef}
                    onChange={event => setValue('address', event.target.value)}
                    value={address}
                    name="address"
                    id="address"
                />
            </FormGroup>
            
            <FormGroup>
                <div id="map" style={styles.map} ref={mapRef} />
            </FormGroup>
        </>
    );
};

export default Location;